import { useEffect, useRef, useState } from "react";

import { OrganizationsService } from "@/modules/dashboard/cards/services/organizationsService";
import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";

/** Mirrors `ORGANIZATION_SEARCH_MIN_LENGTH` in coloca-nest — a shorter term is a 400, not a narrower search. */
export const ORGANIZATION_SEARCH_MIN_LENGTH = 3;

const DEBOUNCE_MS = 350;
const SEARCH_LIMIT = 50;

export interface UseOrganizationSearchResult {
  results: Organization[];
  isSearching: boolean;
  /** The term `results` correspond to. Empty while the input is below the minimum length. */
  activeSearch: string;
  hasError: boolean;
}

/** What the last completed request answered, and for which term. */
interface SettledSearch {
  term: string;
  results: Organization[];
  hasError: boolean;
}

const NOTHING_SETTLED: SettledSearch = { term: "", results: [], hasError: false };

/**
 * Server-side organization search for the payments-config pickers. Hits `GET
 * /internal/organizations?search=`, which matches name, email **and the organization id** over the whole
 * table — so a pasted uuid resolves even when the org falls outside the page `useOrganizationsCatalog`
 * preloads.
 *
 * Deliberately NOT `clients/hooks/useDebouncedOrganizationSearch`, which looks like a duplicate and is not:
 * that one hits `GET /internal/clients/organizations/search?name=`, matches names only, and returns the
 * clients-tree shape. Searching by id is the whole point here.
 *
 * A term below the minimum resolves to no search at all rather than a request the backend would reject — the
 * caller falls back to the preloaded catalog for those keystrokes.
 *
 * Everything the caller reads is DERIVED from the input and the last settled response, so the effect only
 * ever sets state asynchronously (no cascading render on each keystroke). "Searching" is precisely "a term is
 * active and what settled is not it yet", which also means a term whose answer is already in hand never
 * flashes a loading state.
 */
export function useOrganizationSearch(
  input: string,
  debounceMs: number = DEBOUNCE_MS,
): UseOrganizationSearchResult {
  const [settled, setSettled] = useState<SettledSearch>(NOTHING_SETTLED);
  // Guards against out-of-order responses: a slow request for an earlier term must not overwrite the
  // results of a later one.
  const requestIdRef = useRef(0);

  const trimmed = input.trim();
  const term = trimmed.length >= ORGANIZATION_SEARCH_MIN_LENGTH ? trimmed : "";
  const isSettled = settled.term === term;

  // `isSettled` is a dependency rather than a ref read: the effect re-runs when the answer lands and returns
  // right here, so a term whose result is already in hand is never requested twice.
  useEffect(() => {
    if (!term || isSettled) return;

    const id = ++requestIdRef.current;
    const timer = window.setTimeout(() => {
      if (id !== requestIdRef.current) return;

      OrganizationsService.fetchOrganizations({ skip: 0, limit: SEARCH_LIMIT, search: term })
        .then((res) => {
          if (id !== requestIdRef.current) return;
          setSettled({ term, results: res.data.data ?? [], hasError: false });
        })
        .catch(() => {
          if (id !== requestIdRef.current) return;
          // A failed search settles as "nothing found" plus the flag, never as a stale result set that would
          // look like an answer to the current term.
          setSettled({ term, results: [], hasError: true });
        });
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [term, isSettled, debounceMs]);

  return {
    results: isSettled ? settled.results : [],
    isSearching: term !== "" && !isSettled,
    activeSearch: isSettled ? term : "",
    hasError: isSettled && settled.hasError,
  };
}
